/**
 * Avisos de capitulos nuevos.
 *
 * El servidor del VPS vigila la biblioteca y manda notificaciones push. Aqui
 * solo se registra o se da de baja este dispositivo.
 *
 * En iOS esto **solo funciona con la app instalada en la pantalla de inicio**.
 * Abierta como pagina de Safari, `PushManager` ni siquiera existe.
 */

import { cabecerasAuth, ErrorApi } from './cliente'

export interface EstadoAvisos {
  suscripciones: number
  ultimaRevision: number
}

async function pedir<T>(ruta: string, opciones?: RequestInit): Promise<T> {
  let respuesta: Response
  try {
    respuesta = await fetch(ruta, {
      ...opciones,
      headers: { 'Content-Type': 'application/json', ...cabecerasAuth() },
    })
  } catch {
    throw new ErrorApi('No se pudo conectar con el servidor')
  }
  if (!respuesta.ok) {
    throw new ErrorApi(`El servidor respondio ${respuesta.status}`, respuesta.status)
  }
  return (await respuesta.json()) as T
}

/** Si este dispositivo puede recibir avisos. */
export function sePuedeAvisar(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window
}

/** Si la app esta instalada en la pantalla de inicio, requisito en iOS. */
export function estaInstalada(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches
}

export async function suscripcionActual(): Promise<PushSubscription | null> {
  if (!sePuedeAvisar()) return null
  const registro = await navigator.serviceWorker.ready
  return registro.pushManager.getSubscription()
}

/**
 * La clave publica del servidor viaja en base64url y el navegador la quiere
 * en bytes.
 */
// El parametro de Uint8Array no sobra: sin el, TypeScript lo ensancha a
// ArrayBufferLike (que admite SharedArrayBuffer) y `subscribe` pide BufferSource.
function aBytes(base64url: string): Uint8Array<ArrayBuffer> {
  const relleno = '='.repeat((4 - (base64url.length % 4)) % 4)
  const normal = (base64url + relleno).replace(/-/g, '+').replace(/_/g, '/')
  const binario = atob(normal)
  const bytes = new Uint8Array(binario.length)
  for (let i = 0; i < binario.length; i++) bytes[i] = binario.charCodeAt(i)
  return bytes
}

export async function activarAvisos(): Promise<void> {
  if (!sePuedeAvisar()) {
    throw new Error('Este dispositivo no admite avisos')
  }

  // El permiso hay que pedirlo desde un gesto del usuario; de ahi que esto
  // se llame siempre desde el onClick de un boton.
  const permiso = await Notification.requestPermission()
  if (permiso !== 'granted') {
    throw new Error('No diste permiso para los avisos')
  }

  const { clave } = await pedir<{ clave: string }>('/avisos/clave')
  const registro = await navigator.serviceWorker.ready

  const suscripcion =
    (await registro.pushManager.getSubscription()) ??
    (await registro.pushManager.subscribe({
      // Obligatorio: el navegador exige que cada aviso se le ensene al usuario
      userVisibleOnly: true,
      applicationServerKey: aBytes(clave),
    }))

  await pedir('/avisos/suscribir', {
    method: 'POST',
    body: JSON.stringify(suscripcion.toJSON()),
  })
}

export async function desactivarAvisos(): Promise<void> {
  const suscripcion = await suscripcionActual()
  if (!suscripcion) return
  await pedir('/avisos/baja', {
    method: 'POST',
    body: JSON.stringify({ endpoint: suscripcion.endpoint }),
  })
  await suscripcion.unsubscribe()
}

export async function probarAviso(): Promise<number> {
  const { enviados } = await pedir<{ enviados: number }>('/avisos/probar', { method: 'POST' })
  return enviados
}

export async function estadoAvisos(): Promise<EstadoAvisos> {
  return pedir<EstadoAvisos>('/avisos/estado')
}

export type EstadoSalud = 'bien' | 'ojo' | 'mal'

export interface PuntoSalud {
  clave: string
  titulo: string
  estado: EstadoSalud
  detalle: string
}

export interface Salud {
  /** Segundos, no milisegundos: viene de time.time() de Python. */
  generado: number
  estado: EstadoSalud
  puntos: PuntoSalud[]
  /** Lo que el servidor arreglo solo en la ultima revision. */
  reparaciones: string[]
}

/** La ultima revision guardada. Responde al instante, no comprueba nada. */
export async function obtenerSalud(): Promise<Salud> {
  return pedir<Salud>('/avisos/salud')
}

/**
 * Fuerza una revision entera.
 *
 * Es POST y no GET porque no se limita a mirar: desatasca descargas y sale a
 * probar las fuentes una por una, asi que tarda.
 */
export async function revisarSalud(): Promise<Salud> {
  return pedir<Salud>('/avisos/salud', { method: 'POST' })
}

